"use client";

import React, { useState } from "react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { ChevronLeft, ChevronRight, X, Maximize2 } from "lucide-react";
import { API_UPLOAD_URL, getImageUrl } from "@/app/lib/api";

import type { Product } from "./ProductClientView";

export default function ProductGallery({ product }: { product: Product }) {
  const images = Array.isArray(product.images) ? product.images : [];
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);

  const hasDiscount =
    product.promoPrice && product.promoPrice < product.price;

  return (
    <div className="w-full">
      {/* ===== DESKTOP ===== */}
      <div className="hidden md:grid grid-cols-[90px_1fr] gap-6">
        {/* Thumbnails */}
        <div className="flex flex-col gap-3">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`relative aspect-square rounded-xl overflow-hidden border transition
              ${
                active === i
                  ? "border-black"
                  : "border-gray-200 hover:border-gray-400"
              }`}
            >
              <Image
                src={getImageUrl(img)}
                alt=""
                fill
                className="object-cover"
              />
            </button>
          ))}
        </div>

        {/* Main image */}
        <div
          className="relative aspect-square rounded-2xl overflow-hidden bg-white cursor-zoom-in group"
          onClick={() => setOpen(true)}
        >
          {hasDiscount && (
            <span className="absolute top-4 left-4 z-10 bg-black text-white text-xs px-3 py-1 rounded-full">
              -{Math.round(
                ((product.price - product.promoPrice!) / product.price) * 100
              )}
              %
            </span>
          )}

          <Image
            src={getImageUrl(images[active])}
            alt={product.name}
            fill
            priority
            className="object-cover transition-transform duration-500 group-hover:scale-105"
          />

          <button className="absolute bottom-4 right-4 bg-white/90 backdrop-blur px-4 py-2 rounded-full text-sm flex items-center gap-2 shadow">
            <Maximize2 className="w-4 h-4" />
            Zoom
          </button>
        </div>
      </div>

      {/* ===== MOBILE ===== */}
      <div className="md:hidden">
        <Swiper
          pagination={{ clickable: true }}
          modules={[Pagination]}
          onSlideChange={(s) => setActive(s.realIndex)}
          className="rounded-2xl overflow-hidden"
        >
          {images.map((img, i) => (
            <SwiperSlide key={i}>
              <div
                className="relative aspect-square bg-white"
                onClick={() => setOpen(true)}
              >
                <Image
                  src={getImageUrl(img)}
                  alt=""
                  fill
                  className="object-cover"
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* ===== LIGHTBOX ===== */}
      {open && (
        <div
          className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center"
          onClick={() => setOpen(false)}
        >
          <div
            className="relative w-full max-w-4xl h-[85vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 z-20 bg-white rounded-full p-2"
            >
              <X />
            </button>

            <Swiper
              initialSlide={active}
              loop
              className="w-full h-full"
            >
              {images.map((img, i) => (
                <SwiperSlide key={i}>
                  <div className="relative w-full h-full">
                    <Image
                      src={getImageUrl(img)}
                      alt=""
                      fill
                      className="object-contain bg-white rounded-xl"
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      )}
    </div>
  );
}
